import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import NewsletterPopup from "../../components/NewsletterPopup";
import MasterclassPopup from "../../components/MassterclassPopup";
import SimpleRegistrationPopup from "../../components/MassterRegistrationPopup";
import { apiUrls, imageUrl as imgUrl, baseUrl } from "../../utils/url";
import { postRequest } from "../../utils/networking";
import { toast } from "react-toastify";
import CommonToastContainer from "../../components/CommonToastContainer";

import instructorHari from "../../assets/images/instructor_hari.png";
import shoppingCart3d from "../../assets/images/3d_shopping_cart.png";
import amazonLogo from "../../assets/images/Amazon-Logo.png";
import meeshoLogo from "../../assets/images/Meesho-Logo.png";
import flipkartLogo from "../../assets/images/Flipkart_logo.svg";

function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  let path = normalized;
  if (path.startsWith('/')) {
    path = path.substring(1);
  }
  const assetsIndex = path.indexOf("assets/");
  if (assetsIndex !== -1) {
    return path.substring(assetsIndex + 7);
  }

  const asstesIndex = path.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return path.substring(asstesIndex + 7);
  }
  
  if (path.includes("home/")) {
    return path.replace("home/", "");
  }
  return path;
}

const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  let extractedPath = getPath(imagePath);
  return `${imgUrl}/${extractedPath}`;
};

const heroBadges = [
  "80+ Students Launched Their First Product 🚀",
  "60+ Students Got Their First E-Commerce Order 🎉",
  "40+ Students Are Now Receiving Orders Regularly 📦",
  "100+ Beginners Took Their First Step Into E-Commerce",
  "Students From Zero Experience to Their First Sale",
  "From Product Selection to Real Orders — Students Are Doing It",
  "Not Just Learning E-Commerce — Students Are Actually Selling"
];

export default function Hero() {
  const navigate = useNavigate();
  const [badgeIndex, setBadgeIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const badgeTimer = setInterval(() => {
      setBadgeIndex((prev) => (prev + 1) % heroBadges.length);
    }, 3000);
    return () => clearInterval(badgeTimer);
  }, []);
  const [showCheckboxInPopup, setShowCheckboxInPopup] = useState(true);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailText, setEmailText] = useState("");
  const [days, setDays] = useState('00');
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');
  const [formattedDate, setFormattedDate] = useState('');
  const [formattedTime, setFormattedTime] = useState('');
  const targetDateRef = useRef(null);
  const [isInstructorImageLoaded, setIsInstructorImageLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [webinarData, setWebinarData] = useState(null);

  const handleLoginNavigation = () => {
    navigate('/login');
  };

  // WhatsApp phone number - replace with your actual number
  const WHATSAPP_PHONE = "918939326064"; // Format: countrycode + number without +
  const WHATSAPP_MESSAGE = "Hi! I'm interested in learning more about your E-Commerce masterclass.";

  const handleWhatsAppChat = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    const updateCountdown = () => {
      if (!targetDateRef.current) return;
      
      const now = new Date();
      const diff = targetDateRef.current - now;

      if (diff <= 0) { 
        setDays('00'); 
        setHours('00'); 
        setMinutes('00'); 
        setSeconds('00');
        return;
      }

      const daysCalc = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hoursCalc = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutesCalc = Math.floor((diff / (1000 * 60)) % 60);
      const secondsCalc = Math.floor((diff / 1000) % 60);

      setDays(daysCalc.toString().padStart(2, '0'));
      setHours(hoursCalc.toString().padStart(2, '0'));
      setMinutes(minutesCalc.toString().padStart(2, '0'));
      setSeconds(secondsCalc.toString().padStart(2, '0'));
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const openMasterclassPopup = (requireCheckbox = true) => {
    setShowCheckboxInPopup(requireCheckbox);
    setShowPopup(true);
    document.body.style.overflow = "hidden";
  };

  const closeMasterclassPopup = () => {
    setShowPopup(false);
    setShowCheckboxInPopup(true);
    document.body.style.overflow = "";
  };

  const openNewsletterModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEmailSubject("");
    setEmailText("");
    setIsLoading(false);
    setShowNewsletterModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeNewsletterModal = () => {
    setEmailSubject("");
    setEmailText("");
    setIsLoading(false);
    setShowNewsletterModal(false);
    document.body.style.overflow = "";
  };

  const handleGoNext = () => {
    closeMasterclassPopup();
    setShowRegistrationModal(true);
    document.body.style.overflow = "hidden";
  };

  const openRegistrationModal = () => {
    setShowRegistrationModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeRegistrationModal = () => {
    setShowRegistrationModal(false);
    document.body.style.overflow = "";
  };

  const showToast = (message, isSuccess = true) => {
    if (isSuccess) {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  const handleSendNewsletter = async (e) => {
    e.preventDefault();
    
    if (!emailSubject || !emailSubject.includes('@')) {
      showToast("Please enter a valid email address", false);
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await fetch(
        `${baseUrl}/ecomcourse/newsletter/createnewsletter`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: emailSubject,
          }),
        }
      );

      const data = await response.json();
      console.log("API Response:", data);

      if (response.ok) {
        const apiMessage = data.message || data.msg || data.success || data.data?.message || "Subscription successful";
        
        showToast(apiMessage, true);
        
        setTimeout(() => {
          closeNewsletterModal();
        }, 2000);
      } else {
        const errorMessage = data.message || data.error || data.msg || data.data?.message || "Subscription failed. Please try again.";
        
        showToast(errorMessage, false);
        setIsLoading(false);
      }

    } catch (error) {
      console.error("Fetch error:", error);
      showToast("Network error. Please check your connection.", false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showPopup) closeMasterclassPopup();
        if (showNewsletterModal) closeNewsletterModal();
        if (showRegistrationModal) closeRegistrationModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [showPopup, showNewsletterModal, showRegistrationModal]);

  useEffect(() => {
    fetchWebinar();
  }, []);

  const fetchWebinar = async () => {
    try {
      const response = await postRequest(apiUrls.getcurrentwebinar);
      console.log("API", response);

      const webinar = response?.data?.[0];
      if (!webinar) return;

      setWebinarData(webinar);

      const target = new Date(webinar.asondate);
      targetDateRef.current = target;
      const dateOptions = {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      };  
      const timeOptions = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      setFormattedDate(
        target.toLocaleDateString("en-US", dateOptions)
      );

      setFormattedTime(
        target.toLocaleTimeString("en-IN", timeOptions)
      );

    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  return (
    <>
      <section id="hero" className="relative min-h-screen bg-gradient-to-br from-orange-50/20 via-amber-50/30 to-white flex items-center justify-center pt-16 pb-10 lg:pt-14 lg:pb-12 overflow-hidden">
        {/* Background shapes */}
        <div className="shape-bg">
          <div className="shape-1" style={{ background: "radial-gradient(circle at 30% 40%, #fde6d2 0%, #ffe6cc 60%, transparent 80%)" }}></div>
          <div className="shape-2" style={{ background: "radial-gradient(circle, #fdba74 0%, #fde6d2 40%, transparent 70%)" }}></div>
          <div className="shape-3" style={{ background: "#feedd9" }}></div>
          <div className="shape-4" style={{ background: "#fff3e0" }}></div>
        </div>
        <div className="decorative-blur" style={{ background: "#ffedd5", opacity: "0.4" }}></div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10 w-full">
          {/* Main Hero grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Column (Content) */}
            <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left gap-6 relative -mt-4 lg:-mt-8">
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-orange-50/90 border border-orange-200 text-[11px] sm:text-xs font-bold text-orange-600 tracking-wide animate-fade-in-up transition-all duration-300 cursor-default shadow-xs max-w-full">
                <span className="shrink-0 flex items-center justify-center w-4.5 h-4.5 rounded-full bg-orange-500 text-white font-bold text-[10px] animate-pulse">
                  ✓
                </span>
                <div className="relative overflow-hidden h-5 flex items-center justify-start min-w-0">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={badgeIndex}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="truncate sm:whitespace-nowrap"
                    >
                      {heroBadges[badgeIndex]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl lg:text-[40px] font-extrabold text-gray-900 leading-tight">
                Build Your <span className="text-orange-500 whitespace-nowrap">E-Commerce</span> Business <br className="hidden sm:inline" />
                With Practical Guidance.
              </h1>

              {/* Subtitle / Freebies Info */}
              <div className="hidden lg:block space-y-3 max-w-xl text-center lg:text-left mt-2 lg:mt-4">
                <p className="text-xl sm:text-2xl font-bold text-orange-500">
                  ஈஸியா தமிழ்ல கத்துக்கலாம்!
                </p>
                <p className="text-lg sm:text-xl text-gray-600 font-semibold leading-relaxed">
                  Practical e-commerce guidance for beginners and sellers — from choosing the right product to creating listings, running ads, and growing your sales.
                </p>
              </div>

              {/* Buttons Row */}
              <div className="mt-2 lg:mt-4 flex flex-col items-center lg:items-start gap-3">
                {/* Tamil Text (Mobile Only - Above Login Button) */}
                <p className="md:hidden text-lg sm:text-xl font-bold text-orange-500 text-center">
                  ஈஸியா தமிழ்ல கத்துக்கலாம்!
                </p>

                {/* Login Button (Mobile Only) */}
                <button
                  onClick={handleLoginNavigation}
                  className="md:hidden group relative flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-2 px-8 rounded-full transform hover:-translate-y-1 transition-all duration-300 w-fit cursor-pointer shadow-md"
                >
                  <span className="tracking-wide">Login</span>
                </button>

                {/* Join the course Button */}
                <button
                  onClick={handleLoginNavigation}
                  className="shimmer-btn group relative flex items-center justify-center bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-3.5 px-8 rounded-full transform hover:-translate-y-1 transition-all duration-300 w-fit cursor-pointer shadow-md"
                >
                  <span className="tracking-wide">Join the course</span>
                </button>
              </div>

              {/* Large 3D Shopping Cart Asset */}
              <img 
                src={shoppingCart3d} 
                alt="3D Shopping Cart" 
                className="absolute -bottom-16 left-[70%] w-[350px] h-[350px] object-contain pointer-events-none z-0 hidden lg:block animate-float-delayed" 
              />
            </div>

            {/* Right Column (Instructor Card & Masterclass Card) */}
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col items-center justify-center gap-6 w-full z-10 lg:pl-10 pt-6 lg:pt-12">
              
              {/* Instructor Card */}
              <div className="relative rounded-3xl overflow-hidden border border-orange-100 bg-white max-w-[280px] w-full aspect-square relative shrink-0">
                {webinarData?.mentorprofileurl && (
                  <>
                    {!isInstructorImageLoaded && (
                      <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center z-0">
                        <svg className="w-8 h-8 text-orange-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    <img 
                      src={getFullImageUrl(webinarData.mentorprofileurl)} 
                      alt={`Instructor ${webinarData?.mentorname || ""}`}
                      className={`w-full h-full object-cover relative z-10 transition-opacity duration-300 ${isInstructorImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => setIsInstructorImageLoaded(true)}
                    />
                  </>
                )}
                
                {/* Overlay details */}
                <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <p className="text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-0.5">Instructor</p>
                  <h3 className="text-base font-bold text-white leading-tight">{webinarData?.mentorname || ""}</h3>
                  <p className="text-xs text-gray-300">{webinarData?.mentorrole || ""}</p>
                </div>

                {/* Orange Badge with White Star */}
                <div className="absolute bottom-4 right-4 w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center text-white">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>

              {/* Masterclass details card */}
              <div className="bg-white p-5 sm:p-6 rounded-3xl border border-orange-50 w-full max-w-[360px] flex flex-col gap-4">
                
                {/* Title and Date Row */}
                <div>
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1 leading-snug">
                    {webinarData?.webinarname || "Future of E-commerce"}
                  </h3>
                  
                  <div className="flex items-center justify-between mt-2.5 text-xs">
                    <span className="font-bold text-amber-700">{formattedDate || "Wednesday, June 3, 2026"}</span>
                    <span className="flex items-center gap-1 font-semibold text-gray-400">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      {formattedTime || "02:02 pm"}
                    </span>
                  </div>
                </div>

                {/* Countdown Timer Block */}
                <div className="bg-orange-50/40 rounded-2xl p-3 sm:p-4 border border-orange-100/50">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {/* Days */}
                    <div className="flex flex-col items-center">
                      <div className="w-full bg-white rounded-xl py-2 border border-orange-100/40 flex items-center justify-center min-h-[44px]">
                        <span className="text-xl font-extrabold text-orange-600 leading-none">{days}</span>
                      </div>
                      <span className="text-[9px] font-bold text-gray-400 uppercase mt-1.5 tracking-wider">DAYS</span>
                    </div>
                    {/* Hours */}
                    <div className="flex flex-col items-center">
                      <div className="w-full bg-white rounded-xl py-2 border border-orange-100/40 flex items-center justify-center min-h-[44px]">
                        <span className="text-xl font-extrabold text-orange-600 leading-none">{hours}</span>
                      </div>
                      <span className="text-[9px] font-bold text-gray-400 uppercase mt-1.5 tracking-wider">HRS</span>
                    </div>
                    {/* Minutes */}
                    <div className="flex flex-col items-center">
                      <div className="w-full bg-white rounded-xl py-2 border border-orange-100/40 flex items-center justify-center min-h-[44px]">
                        <span className="text-xl font-extrabold text-orange-600 leading-none">{minutes}</span>
                      </div>
                      <span className="text-[9px] font-bold text-gray-400 uppercase mt-1.5 tracking-wider">MINS</span>
                    </div>
                    {/* Seconds */}
                    <div className="flex flex-col items-center">
                      <div className="w-full bg-white rounded-xl py-2 border border-orange-100/40 flex items-center justify-center min-h-[44px]">
                        <span className="text-xl font-extrabold text-orange-600 leading-none">{seconds}</span>
                      </div>
                      <span className="text-[9px] font-bold text-gray-400 uppercase mt-1.5 tracking-wider">SECS</span>
                    </div>
                  </div>
                </div>

                {/* Secondary Border Button - Check Master Class Details */}
                <button
                  onClick={() => openMasterclassPopup(true)}
                  className="w-full bg-white border border-gray-200 hover:border-orange-200 text-gray-700 hover:text-orange-500 text-xs sm:text-sm font-bold py-3.5 px-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Check Master Class Details</span>
                </button>

                {/* Primary Orange Gradient Button - Join The Masterclass Now */}
                <button
                  onClick={openRegistrationModal}
                  disabled={days === '00' && hours === '00' && minutes === '00' && seconds === '00'}
                  className={`w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-xs sm:text-sm font-bold py-3.5 px-4 rounded-2xl transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg ${
                    (days === '00' && hours === '00' && minutes === '00' && seconds === '00')
                      ? 'opacity-50 !cursor-not-allowed grayscale' 
                      : 'transform hover:-translate-y-0.5 cursor-pointer'
                  }`}>
                  <span>Join The Masterclass Now</span>
                  <svg className="w-4 h-4 text-white shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

            </div>
          </div>

          {/* Bottom Features Banner */}
          <div className="w-full mt-16 bg-white rounded-3xl border border-gray-50 p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Feature 1 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500 shrink-0 border border-orange-100/50">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Expert Guidance</h4>
                <p className="text-xs text-gray-500 leading-relaxed">Learn from experienced e-commerce professionals.</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500 shrink-0 border border-orange-100/50">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Proven Strategies</h4>
                <p className="text-xs text-gray-500 leading-relaxed">Real-world strategies to scale your business.</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500 shrink-0 border border-orange-100/50">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Community Support</h4>
                <p className="text-xs text-gray-500 leading-relaxed">Join a growing community of e-commerce learners.</p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500 shrink-0 border border-orange-100/50">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Lifetime Access</h4>
                <p className="text-xs text-gray-500 leading-relaxed">Access resources and updates for lifetime.</p>
              </div>
            </div>

          </div>

          {/* Freebies Banner Section */}
          <div className="w-full mt-12 bg-gradient-to-r from-orange-50/70 via-amber-50/50 to-orange-50/70 rounded-3xl border border-orange-200/80 p-6 sm:p-8 text-center flex flex-col items-center gap-4 shadow-sm">
            <p className="text-base sm:text-lg lg:text-xl text-gray-800 font-medium max-w-3xl leading-relaxed">
              We provide a free <span className="font-bold text-orange-600">Sale Price Calculator</span>,{" "}
              <span className="font-bold text-orange-600">Ecommerce Beginner Roadmap</span>, and many other useful resources for aspiring ecommerce sellers like you!
            </p>
            <div className="inline-flex items-center gap-2 bg-white/90 border border-orange-100 rounded-xl px-4 py-2 text-xs sm:text-sm font-semibold text-gray-700 shadow-xs">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
              <span>To get all freebies, click the <button onClick={openNewsletterModal} className="text-orange-600 font-bold underline cursor-pointer hover:text-orange-700">“Join Now”</button> button below</span>
            </div>
            <button
              onClick={openNewsletterModal}
              className="shimmer-btn group relative flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-xs sm:text-sm font-bold py-3 px-8 rounded-full transform hover:-translate-y-1 transition-all duration-300 w-fit cursor-pointer shadow-md"
            >
              <span className="tracking-wide">Join Now</span>
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white text-orange-500 group-hover:translate-x-0.5 transition-transform duration-300">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </button>
          </div>

          {/* Trust & Stats Section */}
          <div className="w-full mt-12 sm:mt-16 bg-white/80 backdrop-blur-sm rounded-3xl border border-orange-100/50 p-8 sm:p-12">
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {/* Stat 1 */}
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-orange-500">1 Lakh+</div>
                <p className="text-sm text-gray-600 mt-1 font-medium">Followers Across All Social Media</p>
              </div>
              
              {/* Stat 2 */}
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-orange-500">100+</div>
                <p className="text-sm text-gray-600 mt-1 font-medium">Students Trained</p>
              </div>
              
              {/* Stat 3 */}
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-orange-500">50+</div>
                <p className="text-sm text-gray-600 mt-1 font-medium">Brands Served</p>
              </div>
              
              {/* Stat 4 */}
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-orange-500">1Cr+</div>
                <p className="text-sm text-gray-600 mt-1 font-medium">Revenue Generated</p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent my-8"></div>

            {/* Trust Text */}
            <div className="text-center">
              <p className="text-sm md:text-base font-semibold text-gray-500 tracking-wider uppercase">
                TRUSTED BY ASPIRING ENTREPRENEURS ACROSS TAMIL NADU
              </p>
            </div>

            {/* Brand Logos with Images - Consistent sizing */}
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-14 mt-4">
              {/* Amazon */}
              <img 
                src={amazonLogo} 
                alt="Amazon" 
                className="h-7 md:h-7 object-contain opacity-70 hover:opacity-100 hover:scale-105 transition-all duration-300 cursor-default"
              />
              
              {/* Flipkart */}
              <img 
                src={flipkartLogo} 
                alt="Flipkart" 
                className="h-30 md:h-30 object-contain opacity-70 hover:opacity-100 hover:scale-105 transition-all duration-300 cursor-default"
              />
              
              {/* Meesho */}
              <img 
                src={meeshoLogo} 
                alt="Meesho" 
                className="h-7 md:h-7 object-contain opacity-70 hover:opacity-100 hover:scale-105 transition-all duration-300 cursor-default"
              />
            </div>

          </div>

        </div>
      </section>

      <MasterclassPopup 
        isOpen={showPopup}
        onClose={closeMasterclassPopup}
        onGoNext={handleGoNext}
        webinarDescription={webinarData?.webinardescription}
        showCheckbox={showCheckboxInPopup}
      />

      {showNewsletterModal && (
        <div 
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) {
              closeNewsletterModal();
            }
          }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full mx-auto relative ">
            <button 
              onClick={closeNewsletterModal} 
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl font-light z-10 transition-colors"
            >
              &times;
            </button>
            
            <div className="p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 text-center">Free Subscription</h2>
              
              <form onSubmit={handleSendNewsletter} className="space-y-5">
                <div>
                  <label htmlFor="emailSubject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input 
                    type="email" 
                    id="emailSubject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e?.target?.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              
                <button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:transform-none flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </>
                  ) : (
                    "Join Now"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      <CommonToastContainer />

      <SimpleRegistrationPopup 
        isOpen={showRegistrationModal}
        onClose={closeRegistrationModal}
        webinarid={webinarData?.webinarid || webinarData?.id || 1}
        webinarPrice={webinarData?.price || ""}
        webregid={1}
        onSuccess={showToast}
        showPrice={false}
      />
    </>
  );
}